import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom, Observable } from 'rxjs';
import { environment } from 'src/environments/environment.dev';
import { IThreadAgentService, IThreadListItem, IRunThreadResponse } from '../../general-agent/tokens/thread-agent.token';
import { ChatMessagePayload, IMessageFeedback } from '../../general-agent/models/types';
import { IThread, IThreadMetadata, ITokenLimitsAgent, LLM_PROVIDERS, MessageItem } from '../../general-agent/models/model';
import { CHAT_TYPE, THREAD_TYPE } from '../models/chat.model';

@Injectable()
export class ChatService implements IThreadAgentService {
    private http = inject(HttpClient);
    private chatType = inject<THREAD_TYPE>(CHAT_TYPE);
    private apiUrl = environment.apiUrl + '/chat';

    getThreads(parentId: string): Promise<{ success?: boolean; data?: IThreadListItem[] }> {
        return lastValueFrom(
            this.http.get<{ success?: boolean; data?: IThreadListItem[] }>(`${this.apiUrl}/threads`, {
                params: { chatType: this.chatType, parentId },
            })
        );
    }

    runThread(data: ChatMessagePayload & { threadId: string; threadMetadataId: string }): Promise<IRunThreadResponse> {
        return lastValueFrom(
            this.http.post<IRunThreadResponse>(`${this.apiUrl}/run-thread`, {
                ...data,
                chatType: this.chatType,
            })
        );
    }

    pauseThread(threadId: string, threadMetadataId: string): Promise<{ success: boolean }> {
        return lastValueFrom(
            this.http.post<{ success: boolean }>(
                `${this.apiUrl}/pause-thread/${threadId}/thread-metadata/${threadMetadataId}`,
                {},
                { params: { chatType: this.chatType } }
            )
        );
    }

    resumeThread(threadId: string, planFeedback?: any, toolParams?: any[], selectedProvider?: LLM_PROVIDERS, _editedPlan?: any, _revisionFeedback?: any, _reuseDecision?: any): Promise<any> {
        return lastValueFrom(
            this.http.post<any>(`${this.apiUrl}/resume-thread/${threadId}`, {
                chatType: this.chatType,
                planFeedback,
                toolParams,
                selectedProvider,
            })
        );
    }

    getThread(threadId: string): Promise<{
        success: boolean;
        data: {
            thread: IThread;
            threadMetadata: IThreadMetadata[];
            conversation: { messages: MessageItem[] };
            token_limits_agent: ITokenLimitsAgent | null;
            messageFeedbacks: IMessageFeedback[];
        };
    }> {
        return lastValueFrom(
            this.http.get<{
                success: boolean;
                data: {
                    thread: IThread;
                    threadMetadata: IThreadMetadata[];
                    conversation: { messages: MessageItem[] };
                    token_limits_agent: ITokenLimitsAgent | null;
                    messageFeedbacks: IMessageFeedback[];
                };
            }>(`${this.apiUrl}/thread/${threadId}`, {
                params: { chatType: this.chatType },
            })
        );
    }

    updateThreadTitle(threadId: string, title: string): Promise<{ success: boolean; data: IThread }> {
        return lastValueFrom(
            this.http.put<{ success: boolean; data: IThread }>(`${this.apiUrl}/update-thread-title`, { threadId, title })
        );
    }

    deleteThread(threadId: string): Promise<{ success: boolean }> {
        return lastValueFrom(
            this.http.delete<{ success: boolean }>(`${this.apiUrl}/thread/${threadId}`)
        );
    }
}
